/* eslint-disable jsx-a11y/alt-text */
import React from 'react'
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer'
import logoPng from './logo-horizontal.png'

const teal = '#2a9d8f'
const darkText = '#1a1a1a'
const gray = '#555'
const lightGray = '#e0e0e0'
const red = '#c0392b'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: darkText,
    paddingHorizontal: 40,
    paddingTop: 32,
    paddingBottom: 72,
    backgroundColor: '#ffffff',
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: teal,
  },
  logoBlock: {
    width: 120,
  },
  logoImage: {
    width: 160,
    height: 40,
  },
  headerRight: {
    textAlign: 'right',
    fontSize: 9,
    color: gray,
    maxWidth: 350,
  },

  // Title
  titleSection: {
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.5,
  },

  // Info grid
  infoRow: {
    flexDirection: 'row',
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: lightGray,
    paddingBottom: 10,
  },
  infoCell: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 8,
    color: gray,
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
  },

  // Sections
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginTop: 14,
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: lightGray,
    color: darkText,
  },

  // Table
  tableHeader: {
    flexDirection: 'row',
    paddingBottom: 4,
    marginBottom: 2,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: lightGray,
  },
  tableRowPaid: {
    opacity: 0.5,
  },
  colName: {
    flex: 3,
  },
  colPatient: {
    flex: 2,
  },
  colQty: {
    width: 30,
    textAlign: 'center',
  },
  colRight: {
    width: 90,
    textAlign: 'right',
  },
  tableHeaderText: {
    fontSize: 8,
    color: gray,
  },
  tableCell: {
    fontSize: 10,
  },
  tableCellStrike: {
    fontSize: 10,
    textDecoration: 'line-through',
    color: gray,
  },
  paidBadge: {
    fontSize: 7,
    color: red,
    fontFamily: 'Helvetica-Bold',
    marginLeft: 4,
  },
  subtotalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 6,
    marginBottom: 2,
  },
  subtotalLabel: {
    fontSize: 9,
    color: gray,
    marginRight: 8,
  },
  subtotalValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    width: 90,
    textAlign: 'right',
  },

  // Totals block
  totalsSection: {
    marginTop: 20,
    padding: 14,
    backgroundColor: '#f7faf9',
    borderWidth: 1,
    borderColor: '#d6e8e5',
    borderRadius: 6,
  },
  totalsTitle: {
    fontSize: 10,
    color: teal,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.6,
    marginBottom: 8,
  },
  totalsDivider: {
    borderTopWidth: 1,
    borderTopColor: lightGray,
    marginVertical: 7,
  },
  paymentTitle: {
    fontSize: 8,
    color: gray,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.4,
    marginBottom: 3,
  },
  totalsLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  totalsLabel: {
    fontSize: 9,
    color: gray,
  },
  totalsValue: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: darkText,
    width: 110,
    textAlign: 'right',
  },
  totalsFinalLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 3,
    paddingHorizontal: 10,
    paddingVertical: 9,
    backgroundColor: '#e5f4f1',
    borderRadius: 4,
  },
  totalsFinalLabel: {
    fontSize: 11,
    color: teal,
    fontFamily: 'Helvetica-Bold',
  },
  totalsFinalValue: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
    color: teal,
    width: 120,
    textAlign: 'right',
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 32,
    left: 40,
    right: 40,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: lightGray,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: gray,
  },
})

// Helper to format currency values
function formatCurrency(value) {
  if (value === undefined || value === null || value === '') return 'R$ 0,00'
  const num =
    typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value
  return `R$ ${num
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
}

function ItemSection({ title, items, showPatient = true }) {
  if (!items || items.length === 0) return null

  const subtotal = items.reduce((acc, item) => {
    const val =
      typeof item.valor === 'string'
        ? parseFloat(item.valor.replace(',', '.'))
        : item.valor || 0
    return acc + val
  }, 0)

  return (
    <>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.tableHeader}>
        <Text style={[styles.tableHeaderText, styles.colName]}>Descrição</Text>
        {showPatient && (
          <Text style={[styles.tableHeaderText, styles.colPatient]}>
            Paciente
          </Text>
        )}
        <Text style={[styles.tableHeaderText, styles.colQty]}>Qtd</Text>
        <Text style={[styles.tableHeaderText, styles.colRight]}>Valor</Text>
      </View>
      {items.map((item, i) => (
        <View
          key={i}
          style={[styles.tableRow, item.snPago === 'S' && styles.tableRowPaid]}
        >
          <View style={styles.colName}>
            <Text style={styles.tableCell}>{item.nome}</Text>
          </View>
          {showPatient && (
            <Text style={[styles.tableCell, styles.colPatient]}>
              {item.paciente || ''}
            </Text>
          )}
          <Text style={[styles.tableCell, styles.colQty]}>{item.qtd || 1}</Text>
          <Text style={[styles.tableCell, styles.colRight]}>
            {formatCurrency(item.valor)}
          </Text>
        </View>
      ))}
      <View style={styles.subtotalRow}>
        <Text style={styles.subtotalLabel}>Subtotal {title}:</Text>
        <Text style={styles.subtotalValue}>{formatCurrency(subtotal)}</Text>
      </View>
    </>
  )
}

export default function PdfCheckOut({
  proposta = '',
  contratante = '',
  suite = '',
  checkIn = '',
  dependentes = [],
  terapias = [],
  suplementos = [],
  formasPagamento = [],
  totalEstornado = 0,
  produtosAdicionais = [],
  subTotal = 0,
  desconto = 0,
  emissaoData = '',
}) {
  const totalPago = formasPagamento.reduce(
    (total, formaPagamento) => total + Number(formaPagamento?.vlPago || 0),
    0,
  )
  const valorEstornado = Number(totalEstornado || 0)
  const valorPagoLiquido = Math.max(0, totalPago - valorEstornado)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBlock}>
            <Image src={logoPng} style={styles.logoImage} />
          </View>
          <View style={styles.headerRight}>
            <Text>Dr. Édio Schaurich - CRM:</Text>
            <Text>
              Rua Buarque de Macedo, 1030/1000 - Progresso, Montenegro - RS
            </Text>
          </View>
        </View>

        {/* Title */}
        <View style={styles.titleSection}>
          <Text style={styles.title}>CHECK-OUT</Text>
        </View>

        {/* Info row 1 — Proposta + Suite */}
        <View style={styles.infoRow}>
          <View style={styles.infoCell}>
            <Text style={styles.infoLabel}>Proposta</Text>
            <Text style={styles.infoValue}>#{proposta}</Text>
          </View>
          <View style={styles.infoCell}>
            <Text style={styles.infoLabel}>Suíte</Text>
            <Text style={styles.infoValue}>{suite || '-'}</Text>
          </View>
        </View>

        {/* Info row 2 — Contratante + Check-in */}
        <View
          style={[
            styles.infoRow,
            { borderBottomWidth: 2, borderBottomColor: teal },
          ]}
        >
          <View style={{ flex: 2 }}>
            <Text style={styles.infoLabel}>Contratante</Text>
            <Text style={styles.infoValue}>{contratante}</Text>
          </View>
          <View style={styles.infoCell}>
            <Text style={styles.infoLabel}>Data Check-in</Text>
            <Text style={styles.infoValue}>{checkIn || '-'}</Text>
          </View>
        </View>

        {/* Dependentes */}
        {dependentes && dependentes.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Dependentes</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, styles.colName]}>Nome</Text>
              <Text style={[styles.tableHeaderText, styles.colRight]}>CPF</Text>
            </View>
            {dependentes.map((dep, i) => (
              <View key={i} style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.colName]}>
                  {dep?.[1] || dep?.nome || ''}
                </Text>
                <Text style={[styles.tableCell, styles.colRight]}>
                  {dep?.[2] || dep?.cpf || ''}
                </Text>
              </View>
            ))}
          </>
        )}

        {/* Terapias Complementares */}
        <ItemSection title="Terapias" items={terapias} showPatient />

        {/* Suplementos */}
        <ItemSection title="Suplementos" items={suplementos} showPatient />

        {/* Produtos Adicionais */}
        <ItemSection
          title="Produtos Adicionais"
          items={produtosAdicionais}
          showPatient={false}
        />

        {/* Totals */}
        <View style={styles.totalsSection} wrap={false}>
          <Text style={styles.totalsTitle}>RESUMO FINANCEIRO</Text>
          {[
            { label: 'SUBTOTAL', value: subTotal },
            { label: 'DESCONTO (-)', value: desconto },
          ].map((item, idx) => (
            <View key={idx} style={styles.totalsLine}>
              <Text style={styles.totalsLabel}>{item.label}</Text>
              <Text style={styles.totalsValue}>
                {formatCurrency(item.value)}
              </Text>
            </View>
          ))}

          <View style={styles.totalsDivider} />
          <Text style={styles.paymentTitle}>FORMAS DE PAGAMENTO</Text>

          {formasPagamento.map((item, index) => (
            <View key={index} style={styles.totalsLine}>
              <Text style={styles.totalsLabel}>{item.nome}</Text>
              <Text style={styles.totalsValue}>
                R$ {item.vlPago.toFixed(2).replace('.', ',')}
              </Text>
            </View>
          ))}

          <View style={styles.totalsDivider} />
          <View style={styles.totalsFinalLine}>
            <Text style={styles.totalsFinalLabel}>Total pago</Text>
            <Text style={styles.totalsFinalValue}>
              {formatCurrency(totalPago)}
            </Text>
          </View>
          {valorEstornado > 0 && (
            <>
              <View style={styles.totalsLine}>
                <Text style={styles.totalsLabel}>Valor estornado</Text>
                <Text style={styles.totalsValue}>
                  {formatCurrency(valorEstornado)}
                </Text>
              </View>
              <View style={styles.totalsFinalLine}>
                <Text style={styles.totalsFinalLabel}>Valor líquido pago</Text>
                <Text style={styles.totalsFinalValue}>
                  {formatCurrency(valorPagoLiquido)}
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Footer */}
        <View fixed style={styles.footer}>
          <Text style={styles.footerText}>
            SPA TOUR LIFE - APART HOTEL LTDA | 3Sync Sistemas
          </Text>
          <Text style={styles.footerText}>
            {emissaoData
              ? `Emitido em Montenegro, ${emissaoData}.`
              : 'Emitido em Montenegro.'}
          </Text>
        </View>
      </Page>
    </Document>
  )
}
